import { create } from 'zustand';
import api from '../utils/api';
import { useActivityLogStore } from './activityLogStore';

export const useCartStore = create((set, get) => ({
  cart: {
    items: [],
    subtotal: 0,
    discounts: [],
    giftCards: [],
    total: 0
  },
  isLoading: false,

  fetchCart: async () => {
    try {
      const response = await api.get('/cart');
      set({ cart: response.data.cart });
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  },

  addToCart: async (item) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/add', item);
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logProductAdded(item);
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  updateQuantity: async (variantId, quantity, itemIndex) => {
    set({ isLoading: true });
    try {
      const oldItem = get().cart.items[itemIndex];
      const oldQty = oldItem?.quantity || 0;
      // Send the cart snapshot so the server can rebuild it if its in-memory copy was lost to a restart.
      const response = await api.put('/cart/update', { variantId, quantity, itemIndex, cart: get().cart });
      set({ cart: response.data.cart, isLoading: false });
      if (oldItem) useActivityLogStore.getState().logQuantityUpdated(oldItem, oldQty, quantity);
      return { success: true };
    } catch (error) {
      console.error('Error updating cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeFromCart: async (variantId, itemIndex) => {
    set({ isLoading: true });
    try {
      const removedItem = get().cart.items[itemIndex];
      // Send the cart snapshot so the server can rebuild it if its in-memory copy was lost to a restart.
      const response = await api.post('/cart/remove', { variantId, itemIndex, cart: get().cart });
      set({ cart: response.data.cart, isLoading: false });
      if (removedItem) useActivityLogStore.getState().logProductRemoved(removedItem);
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  applyDiscount: async (discountCode, locationId) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/apply-discount', { discountCode, locationId });
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logDiscountApplied(discountCode);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error applying discount:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeDiscount: async (discountCode) => {
    try {
      // Send the current cart snapshot so the server can rebuild it if its in-memory
      // copy was lost to a restart (otherwise removal would 404 with "cart not found").
      const response = await api.post('/cart/remove-discount', { discountCode, cart: get().cart });
      set({ cart: response.data.cart });
      useActivityLogStore.getState().logDiscountRemoved(discountCode);
      return { success: true };
    } catch (error) {
      console.error('Error removing discount:', error);
      return { success: false, error: error.response?.data?.error };
    }
  },

  applyGiftCard: async (giftCardCode) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/apply-gift-card', { giftCardCode });
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logGiftCardApplied(giftCardCode, response.data.giftCard?.balance);
      return { success: true, giftCard: response.data.giftCard };
    } catch (error) {
      console.error('Error applying gift card:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeGiftCard: async (giftCardCode) => {
    try {
      const response = await api.post('/cart/remove-gift-card', { giftCardCode });
      set({ cart: response.data.cart });
      useActivityLogStore.getState().logGiftCardRemoved(giftCardCode);
      return { success: true };
    } catch (error) {
      console.error('Error removing gift card:', error);
      return { success: false, error: error.response?.data?.error };
    }
  },

  clearCart: async () => {
    try {
      await api.post('/cart/clear');
      set({
        cart: {
          items: [],
          subtotal: 0,
          discounts: [],
          giftCards: [],
          total: 0
        }
      });
      useActivityLogStore.getState().logCartCleared();
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: error.response?.data?.error };
    }
  }
}));
